import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ImageOrderingEditor, {
  ImageOrderingValue,
} from "./AssetSyndication/ImageOrderingEditor";

interface Props {
  /** The clientProcess config object (holds iframeImageOrdering). */
  config: Record<string, any>;
  /** Writes a path relative to clientProcess (e.g. "iframeImageOrdering"). */
  handleChange: (path: string, value: any) => void;
  /** Sibling assetSyndication config, used to seed from a syndicator's ordering. */
  syndicationConfig?: Record<string, any>;
}

const SERVICE_CONFIG_RE = /^assetSyndication_(.+)_serviceConfig$/;

/** Pull the syndicators that have a usable imageOrdering out of the sibling
 *  assetSyndication config, for the "Copy from syndication" picker. */
const collectSyndicatorOrderings = (
  syndicationConfig: Record<string, any> | undefined,
): Array<{ service: string; ordering: ImageOrderingValue }> => {
  if (!syndicationConfig) return [];
  const out: Array<{ service: string; ordering: ImageOrderingValue }> = [];
  for (const [key, serviceConfig] of Object.entries(syndicationConfig)) {
    const match = key.match(SERVICE_CONFIG_RE);
    if (!match) continue;
    const ordering = serviceConfig?.imageOrdering;
    if (ordering && typeof ordering === "object" && ordering.ImageOrder) {
      out.push({ service: match[1], ordering });
    }
  }
  return out;
};

const IframeOrderingTab: React.FC<Props> = ({ config, handleChange, syndicationConfig }) => {
  const theme = useTheme();
  const accent = theme.palette.status.accent;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const value: ImageOrderingValue = config.iframeImageOrdering || {};
  const syndicators = useMemo(
    () => collectSyndicatorOrderings(syndicationConfig),
    [syndicationConfig],
  );

  const copyFrom = (ordering: ImageOrderingValue) => {
    // Deep clone so later edits to the iframe ordering don't mutate the
    // syndicator's config object (they share no reference).
    handleChange("iframeImageOrdering", structuredClone(ordering));
    setAnchorEl(null);
  };

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 720 }}>
          Image order used by the public iframe when no image type is in the URL.
          Independent of syndication — use “Copy from syndication” to start from a
          syndicator’s ordering, then adjust.
        </Typography>
      </Stack>

      <Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          disabled={syndicators.length === 0}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ textTransform: "none", color: accent, borderColor: accent }}
        >
          {syndicators.length === 0
            ? "No syndication ordering to copy"
            : "Copy from syndication"}
        </Button>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          {syndicators.map(({ service, ordering }) => (
            <MenuItem key={service} onClick={() => copyFrom(ordering)}>
              {service}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <ImageOrderingEditor
        value={value}
        onChange={(next) => handleChange("iframeImageOrdering", next)}
      />
    </Stack>
  );
};

export default IframeOrderingTab;
